import { some, isNumber, forEach, reduce, values } from 'lodash'

import { centsToDollars } from '../utils/formatters'
import { getServiceChargeLineItem, taxExemptable } from '../utils/orderUtils'

export const makeDiscounts = (remoteOrderTotal, orderingPreference, exemptionOverrides) => {
  const loading = remoteOrderTotal?.loading ?? false

  const discountsApply = true
  const taxApplies = exemptionOverrides?.applySalesTax ?? true
  const serviceChargeApplies = exemptionOverrides?.applyServiceFee ?? true

  const serviceChargeDiscountInCents = remoteOrderTotal?.serviceChargeDiscountInCents
  const baseDiscountInCents = remoteOrderTotal?.discountAmountInCents ?? null
  const discountAmountInCents = serviceChargeApplies ? baseDiscountInCents : baseDiscountInCents - serviceChargeDiscountInCents
  const exemptableTaxAmountInCents = remoteOrderTotal?.exemptableTaxAmountInCents ?? null
  const serviceChargeTaxInCents = remoteOrderTotal?.serviceChargeTaxInCents ?? 0
  const taxAmount = exemptableTaxAmountInCents - (serviceChargeApplies ?  0 : serviceChargeTaxInCents)
  let serviceChargeInCents = remoteOrderTotal?.serviceChargeInCents ?? null

  serviceChargeInCents = serviceChargeInCents + Math.abs(serviceChargeDiscountInCents)

  const isValid = some([ isNumber(discountAmountInCents), isNumber(serviceChargeInCents), exemptionOverrides?.applySalesTax ], Boolean)

  let discount = { label: 'Apply Discount (Not Applicable)', selected: discountsApply, disabled: true, loading }
  let exemptSalesTax = { label: 'Apply Sales Tax (Not Applicable)', selected: taxApplies, disabled: true, loading }
  let exemptServiceFee = { label: 'Apply Service Charge (Not Applicable)', selected: serviceChargeApplies, disabled: false, loading }

  if (!isValid) return [ discount, exemptSalesTax, exemptServiceFee ]

  if (isNumber(discountAmountInCents)) {
    const display = centsToDollars(discountAmountInCents)
    discount.label = `Apply Discount (${display})`
    discount.disabled = false
  }

  if (isNumber(serviceChargeInCents)) {
    const display = centsToDollars(Math.abs(serviceChargeInCents))
    exemptServiceFee.label = `Apply Service Charge (${display})`
  }

  if (isNumber(taxAmount)) {
    const display = centsToDollars(Math.abs(taxAmount))
    exemptSalesTax.label = `Apply Sales Tax (${display})`
    exemptSalesTax.disabled = !orderingPreference.taxExemptBool
  }

  const arrayRepresentation = [ discount, exemptSalesTax, exemptServiceFee ]

  return arrayRepresentation
}

export const makeDefaultExemptions = (remoteOrderTotal, orderingPreference) => {
  return {
    applyDiscount: isNumber(remoteOrderTotal?.discountAmountInCents),
    applySalesTax: !orderingPreference.taxExemptBool,
    applyServiceFee: true
  }
}

export const makeExemptionOverridesFromOrder = (order) => {
  const orderTotalFromOrder = order.orderTotalFromOrder

  // Somehow the flow has wound up here without retrieving details so apply everything.
  if (!orderTotalFromOrder) {
    return {
      applyDiscount: true,
      applySalesTax: true,
      applyServiceFee: true,
    }
  }

  // For now, compare to 0; positivity and negativity are uncertain.
  const exemptions = {
    applyDiscount: !!orderTotalFromOrder.discountAmountInCents && orderTotalFromOrder.discountAmountInCents !== 0,
    applySalesTax: !!orderTotalFromOrder.exemptableTaxAmountInCents && orderTotalFromOrder.exemptableTaxAmountInCents !== 0,
    applyServiceFee: !!orderTotalFromOrder.serviceChargeInCents && orderTotalFromOrder.serviceChargeInCents !== 0,
  }

  return exemptions
}

export const makeSelectedExemptions = (selectedDiscountIndexes) => {
  const indexKeyMap = {
    '0': 'applyDiscount',
    '1': 'applySalesTax',
    '2': 'applyServiceFee',
  }

  const exemptions = {
    applyDiscount: false,
    applySalesTax: false,
    applyServiceFee: false,
  }

  forEach(selectedDiscountIndexes, (selectedIndex) => {
    const key = indexKeyMap[selectedIndex]
    exemptions[key] = true
  })

  return exemptions
}

export const calculateTaxAndTotal = (remoteOrderTotal, exemptions) => {
  const totalAmountInCents = remoteOrderTotal?.totalAmountInCents

  if (isNaN(totalAmountInCents)) {
    return {
      tax: '$0.00',
      total: '$0.00'
    }
  }

  const exemptableTaxAmountInCents = remoteOrderTotal?.exemptableTaxAmountInCents ?? 0
  const serviceCharge = remoteOrderTotal?.serviceChargeInCents ?? 0
  const serviceChargeTaxInCents = remoteOrderTotal?.serviceChargeTaxInCents ?? 0

  const applySalesTax = exemptions?.applySalesTax ?? true
  const applyServiceFee = exemptions?.applyServiceFee ?? true

  // These little guys get displayed separately and need to be extracted from the total.
  const nonExemptableTaxRules = values(remoteOrderTotal?.nonExemptableTaxRules ?? [])
  const displayableNonExemptableTax = reduce(nonExemptableTaxRules, (accumulator, taxRule) => {
    if (!taxRule.display) return accumulator

    return accumulator + taxRule.taxAmountInCents
  }, 0)

  const nonExemptableTaxAmountInCents = Math.max(0, (remoteOrderTotal?.nonExemptableTaxAmountInCents ?? 0) - displayableNonExemptableTax)

  let tax = exemptableTaxAmountInCents + nonExemptableTaxAmountInCents
  let total = totalAmountInCents
  const serviceChargeExemptable = taxExemptable(getServiceChargeLineItem(remoteOrderTotal.lineItems))

  if (!applyServiceFee && !applySalesTax) {
    tax  = Math.max(0, nonExemptableTaxAmountInCents)
    total = totalAmountInCents - serviceCharge - exemptableTaxAmountInCents
  } else if (!applyServiceFee) {
    tax = Math.max(0, exemptableTaxAmountInCents + nonExemptableTaxAmountInCents)
    total = totalAmountInCents - serviceCharge

    if (serviceChargeExemptable) {
      tax = Math.max(0, exemptableTaxAmountInCents + nonExemptableTaxAmountInCents - serviceChargeTaxInCents)
      total -= serviceChargeTaxInCents
    }
  } else if (!applySalesTax) {
    tax = nonExemptableTaxAmountInCents
    total = totalAmountInCents - exemptableTaxAmountInCents
  }

  return {
    tax:  centsToDollars(tax),
    total: centsToDollars(total)
  }
}
